import $ from 'jquery';
import ImageAdapter from "./image";
import InlineAdapter from "./inline";
import VideoAdapter from "./video";
import AjaxAdapter from "./ajax";

const adapaters = {
    image: ImageAdapter,
    inline: InlineAdapter,
    video: VideoAdapter,
    ajax: AjaxAdapter,
}

export class Dialog {
    /**
     * @param options {DialogOptions}
     */
    constructor(options) {
        this.type = options.type || 'image';
        this.addModifier = options.addModifier || null;
        this.items = options.items;
        this.startIndex = 'startIndex' in options
            ? options.startIndex
            : this.items.findIndex(item => 'current' in item && item.current === true);

        this.startIndex = Math.max(this.startIndex, 0); // in case it was -1
        this._currentIndex = this.startIndex;

        if (options.closeButton === undefined) {
            this._generateCloseButton = true;
        } else {
            this._generateCloseButton = false;
            this.$closeButton = $(options.closeButton);
        }


        this.$el = this._createDialogElement();
        this.$root = $(`<div class="popup-root popup-root--${this.type}"></div>`);
        this.$el.appendTo(this.$root);

        if (this.addModifier) {
            this.$root.addClass(`popup-root--${this.addModifier}`);
        }

        this.$container = this.$el.find('.popup__container');
        this.$content = this.$el.find('.popup__content');
        this.$title = this.$el.find('.popup__title');

        /**
         * @type {DialogNav|null}
         * @public
         */
        this.nav = this.items.length > 1 ? this._createNavigation(options) : null;
        if (this.nav) {
            this.nav.$nav.appendTo(this.$container);
        }

        /**
         * @type {ImageAdapter|InlineAdapter}
         * @private
         */
        this.adapter = new adapaters[this.type](this, options);
        this.adapter.setup();
        this._setupEvents();
        this._updateContent();
    }

    /**
     * @returns {JQuery<HTMLDialogElement>}
     * @private
     */
    _createDialogElement() {
        const $html = $(`
            <div class="popup popup--${this.type}">
                <div class="popup__container">
                    <div class="popup__content"></div>
                    <div class="popup__title"></div>
                </div>
            </div>
        `);

        if (this._generateCloseButton) {
            this.$closeButton = $(`
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 34.663 34.663" width="48" height="48" class="popup__close">
                <g fill="currentColor" transform="translate(-6.058 5.101)">
                    <path transform="translate(-5.16 16.77) rotate(-45)"
                          d="M46.775,16.477c.012.561-10.445,1.24-23.356,1.517S.032,18.041.02,17.48s10.445-1.24,23.356-1.516,23.388-.048,23.4.513"/>
                    <path transform="translate(30.039 -27.621) rotate(45)"
                          d="M44.5,33.068c0,.561-9.971.932-22.26.829S0,33.255,0,32.695s9.971-.932,22.26-.829,22.248.642,22.243,1.2"/>
                </g>
            </svg>
            `);
            $html.prepend(this.$closeButton);
            $html.addClass('popup--default-close');
        }

        if (this.addModifier) {
            $html.addClass(`popup--${this.addModifier}`);
        }

        return $html;
    }

    /**
     * @param options {DialogOptions}
     * @returns {DialogNav}
     * @private
     */
    _createNavigation(options) {
        const $nav = $('<div>', {class: 'popup__nav'});
        const $prev = $('<div>', {class: 'popup__nav-arrow popup__nav-arrow--prev'}).appendTo($nav);
        const $next = $('<div>', {class: 'popup__nav-arrow popup__nav-arrow--next'}).appendTo($nav);
        if (!options.$navPrev) {
            options.$navPrev = $('<button>', {class: 'swiper-arrow swiper-arrow--prev'});
            options.$navPrev.text('<<');
        }
        if (!options.$navNext) {
            options.$navNext = $('<button>', {class: 'swiper-arrow swiper-arrow--next'});
            options.$navNext.text('>>');
        }
        options.$navPrev.appendTo($prev);
        options.$navNext.appendTo($next);
        return {$nav, $prev, $next};
    }

    _setupEvents() {
        this.$el.on('dialog:prepare', () => {
            this.$root.addClass('visible');
        });
        this.$el.on('dialog:ready', () => {
            this.$el.addClass('visible');
        });
        this.$el.on('dialog:error', () => {
            this.$el.addClass('visible');
            this._showError('Error loading image');
        });
        this.$el.on('dialog:beforeOpen', () => {
            document.documentElement.classList.add('popup-opened');
        });
        this.$el.on('dialog:closed', () => {
            document.documentElement.classList.remove('popup-opened');
        });

        // checking in capture phase, to avoid bugs with other elements removing the clicked target.
        document.body.addEventListener('click', ev => {
            if (ev.target.closest('.popup') === null) {
                this.close();
            }
        }, {capture: true});

        if (this.nav) {
            this.nav.$prev.on('click', () => {
                if (this.adapter.hasPrev(this._currentIndex)) {
                    this.prev();
                }
            });
            this.nav.$next.on('click', () => {
                if (this.adapter.hasNext(this._currentIndex)) {
                    this.next();
                }
            });
        }
        if (this.$closeButton) {
            this.$closeButton.on('click', ev => {
                ev.preventDefault();
                this.close();
            });
        }
    }

    _showError(text) {
        this.$content.empty();
        this.$title.empty();
        this.$content.append($('<p>', {class: 'popup__error'}).text(text));
    }

    _updateContent() {
        const item = this.adapter.getForIndex(this._currentIndex);
        this.$content.empty();
        this.$content.append(item.$el);
        if (item.title) {
            this.$title.text(item.title);
            this.$title.removeClass('d-none');
        } else {
            this.$title.empty();
            this.$title.addClass('d-none');
        }
        this.$el.trigger('dialog:prepare');
        item.ready().then(() => {
            this.$el.trigger('dialog:ready');
        }).catch(() => {
            this.$el.trigger('dialog:error');
        });

        if (this.nav) {
            this.nav.$next.toggleClass('disabled', !this.adapter.hasNext(this._currentIndex));
            this.nav.$prev.toggleClass('disabled', !this.adapter.hasPrev(this._currentIndex));
        }

    }

    on(...args) {
        return this.$el.on(...args);
    }

    next() {
        this._currentIndex = this.adapter.nextIndex(this._currentIndex);
        this._updateContent();
        this.$el.trigger('dialog:next', [this._currentIndex, this.adapter.getForIndex(this._currentIndex)]);
    }

    prev() {
        this._currentIndex = this.adapter.prevIndex(this._currentIndex);
        this._updateContent();
        this.$el.trigger('dialog:prev', [this._currentIndex, this.adapter.getForIndex(this._currentIndex)]);
    }

    open() {
        this.$el.trigger('dialog:beforeAdd');
        this.$root.appendTo('body');
        this.$el.trigger('dialog:beforeOpen');
        this.$root.addClass('show');
        window.requestAnimationFrame(() => {
            this.$el.trigger('dialog:opened');
        });
    }

    close() {
        this.$el.trigger('dialog:beforeClose');
        this.$el.removeClass('visible');
        this.$root.removeClass('visible');
        window.setTimeout(() => {
            this.$root.removeClass('show');
            this.$el.trigger('dialog:closed');
            this.remove();
        }, 300);
    }

    remove() {
        this.$el.trigger('dialog:beforeRemove');
        this.$root.remove();
    }
}
